// Submitting an item to the bank (RFC-056 §3): a kit's tar.gz — its hidden
// part with it — uploaded straight to the exam server, with a note for the
// admin who reviews it and the bank's terms agreed; or the line to paste,
// which makes an item of a kit's question and submits it from byj. A dialog
// over the items, or over an item's page for its next version
// (byj-1wv.51.18): the kit picked with a button, as the Files page picks a
// file, its name said beside it.
import { useRef, useState } from "react";
import type { BankClient } from "../bank";
import { t } from "../i18n";
import { CommandLine } from "./CommandLine";
import { FormDialog } from "./FormDialog";

export const submitDialogId = "bank-submit-dialog";

/** The submit dialog: a new item, or `item`'s next version; `onDone` is given the item it went to. */
export function SubmitDialog({ client, item, onDone, onClose }: { client: BankClient; item?: string; onDone: (id: string) => void; onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState("");
  const [agreed, setAgreed] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  return (
    <FormDialog
      id={submitDialogId}
      testid="bank-submit"
      title={item ? t("bank:submitVersionTitle", { id: item }) : t("bank:submitTitle")}
      submitLabel={t("bank:submit")}
      canSubmit={!!file && agreed}
      onSubmit={async () => {
        const r = await client.submit(file!, note.trim(), item);
        onDone(r.id);
      }}
      onClose={onClose}
    >
      <p className="muted small flush">{t("bank:submitLead")}</p>
      <div className="field">
        <span>{t("bank:submitKit")}</span>
        <div className="row wrap file-pick">
          <button type="button" onClick={() => input.current?.click()}>
            {file ? t("bank:submitKitAgain") : t("bank:submitKitPick")}
          </button>
          <span className={file ? "small" : "muted small"} data-testid="bank-submit-file">
            {file?.name ?? t("bank:submitKitNone")}
          </span>
        </div>
        <span className="muted small">{t("bank:submitKitHint")}</span>
        <input
          ref={input}
          type="file"
          hidden
          accept=".tar.gz,.tgz,application/gzip"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          aria-label={t("bank:submitKit")}
        />
      </div>
      <label className="field">
        {t("bank:submitNote")}
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} aria-label={t("bank:submitNote")} />
      </label>
      <label className="toggle small">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
        {t("bank:submitTerms")}
      </label>
      <div className="stack tight">
        <p className="muted small flush">{t("bank:submitFromByj")}</p>
        {/* byj finds the item a kit was submitted as in the kit itself, so a new version is the same line */}
        <CommandLine command="byj exam bank submit KIT QUESTION --note '…'" />
      </div>
    </FormDialog>
  );
}
